import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  useBlocker,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';

import { KaseApi } from '@/api';
import { IcArrowLeft, IcArrowRight, IcPopUp } from '@/assets/images';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  UnsavedChangesModal,
  VerticalTabs,
  VerticalTabsContent,
  VerticalTabsList,
  VerticalTabsTrigger,
} from '@/components/TailwindComponents';
import { useFormConfigLoader, useKaseContext } from '@/hooks';
import {
  PageBackHeader,
  TabAnimationWrapper,
  useTabLayoutContext,
} from '@/layouts/TabLayout';
import { wasArrestedItems } from '@/layouts/TabLayout/MasterNames/data';
import { IArrestee, IMasterNamePerson, IOffenseOption, Role } from '@/models';
import useMasterIndexAccess from '@/pages/MasterIndex/useMasterIndexAccess';
import { isAnyTargetRole } from '@/services';
import { ConfirmTypes, useToastStore } from '@/store';
import { formatPersonName } from '@/utils';

import { useKaseArrestsContext } from '../../KaseArrestsContext';
import ArrestModal from '../ArrestModal';
import { arrestEditTabs } from './data';
import {
  useArrestEditData,
  useArrestFormInitialization,
  useOffenseSync,
  useArrestSubmit,
  useArrestNarratives,
} from './hooks';
import {
  AdministrativeInfoTab,
  ArresteePersonalInfoTab,
  ArrestInformationTab,
  AssociatedPersonsTab,
  NarrativeTab,
  OffenseDetailsTab,
  SignaturesVerificationTab,
} from './tabs';
import { narrativeToEditorState } from './tabs/NarrativeTab';
import { ArrestEditTab, getDefaultFormValues, IArrestFormData } from './types';
import { arrestFormValidation } from './validation';
import { KaseArrestsScreens } from '../../data';

const UNSAVED_CHANGES_MESSAGE =
  'You have unsaved changes. Are you sure you want to leave this page?';

const ModalLayoutWrapper: FC<PropsWithChildren> = ({ children }) => (
  <div className="flex flex-col h-full">{children}</div>
);

interface KaseArrestEditProps {
  kaseIdProp?: string;
  onBack?: () => void;
  inModal?: boolean;
  navigation?: {
    canPrev: boolean;
    canNext: boolean;
    onPrev: () => void;
    onNext: () => void;
  };
}

const KaseArrestEdit = ({
  kaseIdProp,
  onBack,
  inModal,
  navigation,
}: KaseArrestEditProps = {}) => {
  const { kaseId: kaseIdFromParams = '' } = useParams();
  const kaseId = kaseIdProp ?? kaseIdFromParams;
  const {
    currentArrestee,
    setCurrentArrestee,
    initialEditMode: contextInitialEditMode,
  } = useKaseArrestsContext();
  const { isReadOnlyArrestees } = useKaseContext();
  const { updateCurrentScreen } = useTabLayoutContext();
  const navigate = useNavigate();
  const { updateToast } = useToastStore();

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ArrestEditTab>(
    ArrestEditTab.ADMINISTRATIVE,
  );

  const [isEditMode, setIsEditMode] = useState(contextInitialEditMode);
  const [isUnsavedModalOpen, setIsUnsavedModalOpen] = useState(false);
  const [isSavingBeforeContinue, setIsSavingBeforeContinue] = useState(false);
  const pendingNavigationRef = useRef<{
    proceed?: () => void;
    cancel?: () => void;
  } | null>(null);
  const blockedActionRef = useRef<'POP' | 'PUSH' | 'REPLACE' | null>(null);
  const blockedLocationRef = useRef<string | null>(null);
  const allowNextNavigationRef = useRef(false);
  const usedBrowserPromptRef = useRef(false);

  const { filteredOptions: filteredOffenseOptions } = useFormConfigLoader({
    formName: 'ARREST',
    optionsName: 'OFFENSE',
    category: 'ARRESTS',
  });
  const { allowed: masterIndexAllowedSections } = useMasterIndexAccess();
  const canEditLinkedPerson = masterIndexAllowedSections.includes('persons');

  // Custom hooks for data fetching
  const {
    users,
    agencies,
    courts,
    closedCfsOptions,
    penalCodes,
    offensesByPenalCode,
    associatedPersons,
    narratives,
    otherArrests,
    caseProperties,
    fetchNarratives,
    refetchCourts,
  } = useArrestEditData({
    kaseId,
    currentArrestee,
  });

  const policeUsers = users.filter((user) =>
    isAnyTargetRole(user, [Role.RMS_CASES_FULL_ACCESS, Role.RMS_CASES_VIEW]),
  );

  // Narrative state management
  const arrestNarratives = useArrestNarratives({
    kaseId,
    arresteeId: currentArrestee?.masterName?._id?.toString(),
    narratives,
    fetchNarratives,
    narrativeToEditorState,
  });
  const shouldWarnOnNarrativeLeave =
    activeTab === ArrestEditTab.NARRATIVE &&
    arrestNarratives.hasUnsavedNarratives;

  // Form setup
  const methods = useForm<IArrestFormData>({
    resolver: yupResolver(arrestFormValidation) as any,
    mode: 'all',
    defaultValues: getDefaultFormValues(currentArrestee),
  });

  const { handleSubmit, reset } = methods;

  // Custom hooks for form logic
  const { originalArresteeFullRef, handleCancel: handleFormCancel } =
    useArrestFormInitialization({
      currentArrestee,
      otherArrests,
      reset,
    });

  const { syncArrestChargesToOffenses } = useOffenseSync({ kaseId });
  const {
    isSaving,
    handleSubmit: handleFormSubmit,
    onInvalidSubmit,
  } = useArrestSubmit({
    kaseId,
    currentArrestee,
    filteredOffenseOptions,
    syncArrestChargesToOffenses,
    saveNarratives: () => arrestNarratives.saveAll(),
    onSuccess: () => handleGoBack(),
  });

  // Handle scroll to field if fieldKey is in URL
  useEffect(() => {
    if (searchParams.get('fieldKey')) {
      const element = document.querySelector('.reportRequired');
      setActiveTab(ArrestEditTab.ARREST_INFO);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }
  }, [filteredOffenseOptions, searchParams]);

  const handleGoBack = () => {
    // Branch 1: NIBRS back navigation (Cases only) — preserved
    if (searchParams?.get('viewArresteeId')) {
      navigate(-1);
      return;
    }
    // Branch 2: NIBRS field deep-link (Cases only) — preserved
    if (searchParams?.get('fieldKey')) {
      setCurrentArrestee(undefined);
      updateCurrentScreen(KaseArrestsScreens.KaseArrestList);
      navigate(`/cases/${kaseId}/nibrs`);
      return;
    }
    // Branch 3 default: Tools path uses onBack; Cases path uses the original navigate + updateCurrentScreen
    if (onBack) {
      onBack();
      return;
    }
    navigate(`/cases/${kaseId}/arrests`);
    updateCurrentScreen(KaseArrestsScreens.KaseArrestList);
  };

  const handleCancel = () => {
    requestNarrativeNavigation(() => {
      handleFormCancel();
      arrestNarratives.resetAll();
      // Restore the original full arrestee (including arrestCharges)
      if (originalArresteeFullRef.current) {
        setCurrentArrestee(originalArresteeFullRef.current);
      }
      setIsEditMode(false);
    });
  };

  const requestNarrativeNavigation = useCallback(
    (action?: () => void) => {
      if (!shouldWarnOnNarrativeLeave) {
        action?.();
        return;
      }

      pendingNavigationRef.current = { proceed: action };
      setIsUnsavedModalOpen(true);
    },
    [shouldWarnOnNarrativeLeave],
  );

  const handleCancelNarrativeEdits = useCallback(() => {
    requestNarrativeNavigation(() => {
      arrestNarratives.resetAll();
    });
  }, [arrestNarratives, requestNarrativeNavigation]);

  const clearPendingNavigation = useCallback(() => {
    pendingNavigationRef.current = null;
    blockedActionRef.current = null;
    blockedLocationRef.current = null;
    usedBrowserPromptRef.current = false;
    setIsUnsavedModalOpen(false);
  }, []);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation, historyAction }) => {
      const nextPath = `${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`;
      const isSameLocation =
        nextLocation.pathname === currentLocation.pathname &&
        nextLocation.search === currentLocation.search &&
        nextLocation.hash === currentLocation.hash;

      blockedActionRef.current = historyAction;
      blockedLocationRef.current = nextPath;

      if (
        allowNextNavigationRef.current ||
        !shouldWarnOnNarrativeLeave ||
        isSameLocation
      ) {
        usedBrowserPromptRef.current = false;
        return false;
      }

      if (historyAction === 'POP') {
        usedBrowserPromptRef.current = true;
        return !window.confirm(UNSAVED_CHANGES_MESSAGE);
      }

      usedBrowserPromptRef.current = false;
      return true;
    },
  );

  const prevBlockerStateRef = useRef(blocker.state);
  useEffect(() => {
    const prev = prevBlockerStateRef.current;
    prevBlockerStateRef.current = blocker.state;

    if (blocker.state !== 'blocked' || prev === 'blocked') {
      return;
    }

    if (blockedActionRef.current === 'POP' && usedBrowserPromptRef.current) {
      blocker.reset();
      usedBrowserPromptRef.current = false;
      blockedLocationRef.current = null;
      return;
    }

    const blockedLocation = blockedLocationRef.current;
    pendingNavigationRef.current = {
      proceed: () => {
        if (blockedLocation) {
          navigate(blockedLocation);
        }
      },
      cancel: () => blocker.reset(),
    };
    setIsUnsavedModalOpen(true);
  }, [blocker, navigate]);

  useEffect(() => {
    if (!shouldWarnOnNarrativeLeave) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldWarnOnNarrativeLeave]);

  useEffect(() => {
    return () => {
      blocker.reset?.();
    };
  }, [blocker]);

  useEffect(() => {
    if (shouldWarnOnNarrativeLeave || !allowNextNavigationRef.current) {
      return;
    }

    allowNextNavigationRef.current = false;
  }, [shouldWarnOnNarrativeLeave]);

  const handleDiscardChanges = useCallback(() => {
    pendingNavigationRef.current?.cancel?.();
    arrestNarratives.resetAll();
    clearPendingNavigation();
    setActiveTab(ArrestEditTab.NARRATIVE);
  }, [arrestNarratives, clearPendingNavigation]);

  const handleSaveAndContinue = useCallback(async () => {
    if (!pendingNavigationRef.current?.proceed) {
      clearPendingNavigation();
      return;
    }

    setIsSavingBeforeContinue(true);

    try {
      await arrestNarratives.saveAll();
      updateToast({
        type: ConfirmTypes.SUCCESS,
        open: true,
        message: 'Narratives saved successfully',
      });
      allowNextNavigationRef.current = true;
      const proceed = pendingNavigationRef.current.proceed;
      clearPendingNavigation();
      proceed();
    } catch (error: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: error?.message || 'Failed to save narratives',
      });
      pendingNavigationRef.current?.cancel?.();
      clearPendingNavigation();
      setActiveTab(ArrestEditTab.NARRATIVE);
    } finally {
      setIsSavingBeforeContinue(false);
    }
  }, [arrestNarratives, clearPendingNavigation, updateToast]);

  const renderTabContent = (tabId: ArrestEditTab) => {
    const viewMode = !isEditMode || isReadOnlyArrestees;

    switch (tabId) {
      case ArrestEditTab.ADMINISTRATIVE:
        return <AdministrativeInfoTab viewMode={viewMode} />;
      case ArrestEditTab.PERSONAL_INFO:
        return (
          <ArresteePersonalInfoTab
            viewMode={viewMode}
            canEditPerson={canEditLinkedPerson}
          />
        );
      case ArrestEditTab.ARREST_INFO:
        return (
          <ArrestInformationTab
            viewMode={viewMode}
            users={users}
            policeUsers={policeUsers}
            filteredOffenseOptions={filteredOffenseOptions as IOffenseOption[]}
            agencies={agencies}
            courts={courts}
            closedCfsOptions={closedCfsOptions}
            otherArrests={otherArrests}
            caseProperties={caseProperties}
            refetchCourts={refetchCourts}
          />
        );
      case ArrestEditTab.OFFENSE_DETAILS:
        return (
          <OffenseDetailsTab
            viewMode={viewMode}
            penalCodes={penalCodes}
            offensesByPenalCode={offensesByPenalCode}
          />
        );
      case ArrestEditTab.ASSOCIATED_PERSONS:
        return <AssociatedPersonsTab associatedPersons={associatedPersons} />;
      case ArrestEditTab.NARRATIVE:
        return (
          <NarrativeTab
            {...arrestNarratives}
            narratives={narratives}
            isFormEditMode={isEditMode}
            viewMode={viewMode}
            onCancelEdits={handleCancelNarrativeEdits}
          />
        );
      case ArrestEditTab.SIGNATURES_VERIFICATION:
        return <SignaturesVerificationTab viewMode={viewMode} />;
      default:
        return null;
    }
  };

  const arresteeName = currentArrestee?.masterName
    ? formatPersonName(currentArrestee.masterName as IMasterNamePerson)
    : '';

  const handleArrestStatusUp = async (selectedValue: string, id: string) => {
    const res = await KaseApi.updateArresteeArrestStatus(id, +selectedValue);
    setIsEditMode(false);
    setCurrentArrestee((prev) =>
      prev
        ? ({
            ...prev,
            masterName: {
              ...prev.masterName,
              KaseMasterName: {
                ...prev.masterName.KaseMasterName,
                additionalInfo: {
                  ...(res.data as IArrestee & { additionalInfo?: any })
                    .additionalInfo,
                },
              },
            },
          } as IArrestee)
        : prev,
    );
  };

  const OuterWrapper = inModal ? ModalLayoutWrapper : TabAnimationWrapper;

  return (
    <OuterWrapper>
      <Box
        sx={{
          '& .arrest-nav-arrows': {
            opacity: 0,
            transition: 'opacity 0.2s ease-in-out',
          },
          '&:hover .arrest-nav-arrows': {
            opacity: 1,
          },
        }}
      >
        <PageBackHeader
          title={arresteeName}
          goBack={() => requestNarrativeNavigation(handleGoBack)}
          style={{ fontWeight: 500 }}
        >
          {navigation && (
            <Stack
              className="arrest-nav-arrows"
              direction="row"
              alignItems="center"
              spacing={0.5}
              ml={1}
            >
              <Tooltip title="Previous">
                <span>
                  <IconButton
                    aria-label="previous arrest"
                    size="small"
                    disabled={!navigation.canPrev}
                    onClick={navigation.onPrev}
                    sx={{
                      padding: '4px',
                      '&:disabled': { opacity: 0.3 },
                    }}
                  >
                    <IcArrowLeft />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next">
                <span>
                  <IconButton
                    aria-label="next arrest"
                    size="small"
                    disabled={!navigation.canNext}
                    onClick={navigation.onNext}
                    sx={{
                      padding: '4px',
                      '&:disabled': { opacity: 0.3 },
                    }}
                  >
                    <IcArrowRight />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          )}
          <div className="flex items-center gap-2 m-2">
            <span
              onClick={() =>
                navigate(
                  `/master-index/persons/${currentArrestee?.masterName?._id}/view/information`,
                )
              }
              className="cursor-pointer hover:text-m-primary"
              title={`View ${arresteeName} in Master Index`}
            >
              <IcPopUp />
            </span>
            - {currentArrestee?.arrestId ?? currentArrestee?.number}
          </div>
          <div className="flex ml-auto mr-0">
            {currentArrestee?.masterName?.KaseMasterName?.additionalInfo
              ?.wasArrested === 0 && (
              <Alert
                variant="outlined"
                severity="info"
                sx={{ py: 0, height: '40px', lineHeight: '20px' }}
              >
                This arrest report is in view-only mode because the arrest
                status is set to &ldquo;Not Arrested.&rdquo;
              </Alert>
            )}
            <div className="w-[200px] ml-2">
              <Select
                onValueChange={(selectedValue) => {
                  void handleArrestStatusUp(
                    selectedValue,
                    currentArrestee?.masterName?.KaseMasterName?._id ?? '',
                  );
                }}
                value={
                  currentArrestee?.masterName?.KaseMasterName?.additionalInfo
                    ?.wasArrested != null
                    ? String(
                        currentArrestee.masterName.KaseMasterName
                          ?.additionalInfo?.wasArrested,
                      )
                    : ''
                }
              >
                <SelectTrigger style={{ height: '40px' }}>
                  <SelectValue placeholder="Arrest state unknown." />
                </SelectTrigger>
                <SelectContent>
                  {wasArrestedItems.map(({ label, value: optionValue }) => (
                    <SelectItem key={optionValue} value={String(optionValue)}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {!isEditMode && !isReadOnlyArrestees && (
            <Button
              color="error"
              onClick={() => setIsEditMode(true)}
              className="ml-4 mr-0"
              disabled={
                currentArrestee?.masterName?.KaseMasterName?.additionalInfo
                  ?.wasArrested === 0
              }
            >
              Edit
            </Button>
          )}
        </PageBackHeader>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <FormProvider {...methods}>
        {/* Tabs - height constrained, scrollable */}
        <div
          className={
            inModal
              ? 'flex flex-col flex-1 min-h-0'
              : 'flex flex-col h-[calc(100vh-335px)]'
          }
        >
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Vertical Tabs Navigation */}
            <VerticalTabs
              value={activeTab}
              onValueChange={(value) =>
                requestNarrativeNavigation(() =>
                  setActiveTab(value as ArrestEditTab),
                )
              }
              className="w-full"
            >
              <VerticalTabsList className="w-1/6 min-w-[200px] max-w-[280px] bg-background overflow-y-auto">
                {arrestEditTabs.map((tab) => (
                  <VerticalTabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 data-[state=active]:bg-m-primary data-[state=active]:text-white"
                  >
                    {tab.label}
                  </VerticalTabsTrigger>
                ))}
              </VerticalTabsList>

              {/* Tab Content */}
              {arrestEditTabs.map((tab) => (
                <VerticalTabsContent
                  key={tab.id}
                  value={tab.id}
                  className={
                    tab.id === ArrestEditTab.PERSONAL_INFO
                      ? // forceMount keeps Controllers alive so RHF form values are
                        // preserved across outer-tab navigation; the Tailwind
                        // data-attribute variant hides the panel when inactive so
                        // it doesn't overlap other tab content.
                        'w-5/6 min-h-0 overflow-y-auto p-6 mb-24 data-[state=inactive]:hidden'
                      : 'w-5/6 min-h-0 overflow-y-auto p-6 mb-24'
                  }
                  {...(tab.id === ArrestEditTab.PERSONAL_INFO
                    ? { forceMount: true }
                    : {})}
                >
                  {renderTabContent(tab.id)}
                </VerticalTabsContent>
              ))}
            </VerticalTabs>
          </div>
        </div>

        {/* Save/Cancel Footer - OUTSIDE, sticky at bottom */}
        {isEditMode && (
          <div className="sticky bottom-0 left-0 z-10 w-full px-4 py-2 bg-white border-t border-gray-200 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                color="error"
                onClick={handleSubmit(handleFormSubmit, onInvalidSubmit)}
                disabled={isSaving}
                className="relative"
              >
                {isSaving && (
                  <CircularProgress
                    size={16}
                    className="absolute left-3"
                    sx={{ color: 'white' }}
                  />
                )}
                <span className={isSaving ? 'ml-6' : ''}>Save</span>
              </Button>
            </div>
          </div>
        )}
      </FormProvider>

      {/* Arrest Modal for charge/file/description drawers */}
      <ArrestModal />
      <UnsavedChangesModal
        isOpen={isUnsavedModalOpen}
        onClose={clearPendingNavigation}
        onCancel={handleDiscardChanges}
        onConfirm={() => {
          void handleSaveAndContinue();
        }}
        title="You have unsaved changes"
        description="Please save your changes before leaving the page."
        confirmText="Save and Continue"
        cancelText="Discard changes"
        isLoading={isSavingBeforeContinue}
      />
    </OuterWrapper>
  );
};

export default KaseArrestEdit;
